<template>
  <MMPopupDefault class="bnpl-modal" @close="$emit('close')">
    <template #header>
      <div class="modal-header">
        <div class="modal-icon">
          <img
            v-if="optionInfo?.titleImage"
            :src="optionInfo.titleImage"
            class="modal-icon-image"
          />
        </div>
      </div>
    </template>

    <div class="modal-content">
      <div class="modal-text-section">
        <div class="modal-title" v-html="MODAL_TITLE" />
        <div class="modal-description" v-html="MODAL_DESCRIPTION" />
      </div>

      <div v-if="paymentDates && paymentDates.length > 0" class="payment-schedule">
        <div v-for="(payment, index) in paymentDates" :key="index" class="payment-item">
          <div class="payment-content">
            <div class="payment-info">
              <div class="payment-amount">{{ payment.amount }} ₽</div>
              <div v-if="index > 0" class="payment-date">{{ formatDate(payment.date) }}</div>
              <div v-else class="payment-date payment-date--now">сейчас</div>
            </div>
            <div class="payment-number">
              {{ index + 1 }}
              <div
                v-for="quarterIndex in Array.from({ length: index + 1 }, (_, i) => i + 1)"
                :key="`quarter-${index}-${quarterIndex}`"
                class="payment-quarter"
                :class="`payment-quarter-${quarterIndex}`"
              />
            </div>
          </div>
          <div v-if="index < paymentDates.length - 1" class="payment-separator" />
        </div>
      </div>

      <div class="modal-info-section">
        <div class="info-item">
          <IconCheck :size="20" color="positive" />
          <div class="info-text" v-html="INFO_TEXT_1" />
        </div>
        <div class="info-item">
          <IconCheck :size="20" color="positive" />
          <div class="info-text" v-html="INFO_TEXT_2" />
        </div>
      </div>
    </div>
  </MMPopupDefault>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { MMPopupDefault } from '@mm-frontend/mm-web-ui-kit';
import { IconCheck } from '@/features/ui-kit/icons';
import type { IProductOptionInfo } from '@/pages/index/-src/shared/types/product';
import { formatDeliveryDate } from '@/shared/helpers/formatDeliveryDate';

const MODAL_TITLE = 'Оплачивайте покупки&nbsp;по&nbsp;частям';
const MODAL_DESCRIPTION =
  'Оплатите 25% сегодня&nbsp;и забирайте товары сразу. Оставшаяся сумма спишется тремя равными частями&nbsp;в две недели';
const INFO_TEXT_2 = 'Никаких дополнительных платежей&nbsp;– как обычная оплата картой';

interface Props {
  onShowData: {
    optionInfo: IProductOptionInfo;
  };
}

const props = defineProps<Props>();

defineEmits<{
  close: [];
}>();

const optionInfo = computed(() => props.onShowData?.optionInfo);

const paymentDates = computed(() => {
  if (!optionInfo.value) return null;

  return optionInfo.value.dolyamePaymentDates || optionInfo.value.podeliPaymentDates || null;
});

const isPodeli = computed(() => {
  return optionInfo.value?.podeliPaymentDates && optionInfo.value.podeliPaymentDates.length > 0;
});

const isDolyame = computed(() => {
  return optionInfo.value?.dolyamePaymentDates && optionInfo.value.dolyamePaymentDates.length > 0;
});

const INFO_TEXT_1 = computed(() => {
  const maxAmount = isPodeli.value ? '30&nbsp;000' : '100&nbsp;000';
  return `Доступно&nbsp;для заказов&nbsp;от 100&nbsp;₽&nbsp;до ${maxAmount}&nbsp;₽`;
});

function formatDate(date: number | string): string {
  const dateObj =
    typeof date === 'number'
      ? new Date(date)
      : new Date(typeof date === 'string' && /^\d+$/.test(date) ? parseInt(date, 10) : date);

  const formatted = formatDeliveryDate(dateObj, true);
  return formatted || dateObj.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}
</script>

<style scoped lang="stylus">
.bnpl-modal
  width 100%
  max-width 600px
  max-height 80vh

  +Media(Mobile)
    max-width calc(100vw - 32px)
    max-height calc(100vh - 32px)

.modal-header
  display flex
  justify-content center
  align-items center
  padding 16px 0

.modal-icon
  display flex
  align-items center
  justify-content center

.modal-icon-image
  height 30px
  object-fit contain

.modal-content
  padding 0 16px 24px
  max-height 60vh
  overflow-y auto

  +Media(Mobile)
    max-height calc(100vh - 120px)

.modal-text-section
  text-align left
  margin-bottom 24px

.modal-title
  margin-bottom 8px
  color #141415
  font-size 20px
  font-weight 600
  line-height 28px

.modal-description
  line-height 20px
  color #76797F
  font-size 14px
  font-weight 400

.payment-schedule
  display flex
  flex-direction row
  align-items flex-start
  justify-content space-between
  margin-bottom 24px
  flex-wrap wrap
  gap 0

.payment-item
  display flex
  flex-direction row
  align-items flex-start
  position relative
  flex 1 1 0
  min-width 0

.payment-content
  display flex
  flex-direction column
  align-items center
  width 100%
  position relative
  padding-bottom 2px

.payment-info
  display flex
  flex-direction column
  align-items center
  gap 4px
  margin-bottom 12px
  text-align center

.payment-amount
  font-size 16px
  font-weight 600
  line-height 20px
  color #141415

.payment-date
  font-size 14px
  font-weight 400
  line-height 18px
  color #76797F

  &--now
    color #141415
    font-weight 500

.payment-number
  position relative
  background #F2F4F7
  color #1F1F26
  width 46px
  height 46px
  border-radius 50%
  display flex
  align-items center
  justify-content center
  font-size 14px
  font-weight 600
  line-height 20px
  flex-shrink 0
  margin 0 auto

.payment-quarter
  position absolute
  width 50%
  height 50%

.payment-quarter-1
  border-radius 0 100% 0 0
  right 0
  top 0
  border-right 2px solid #000
  border-top 2px solid #000

.payment-quarter-2
  border-radius 0 0 100% 0
  right 0
  bottom 0
  border-right 2px solid #000
  border-bottom 2px solid #000

.payment-quarter-3
  border-radius 0 0 0 100%
  left 0
  bottom 0
  border-left 2px solid #000
  border-bottom 2px solid #000

.payment-quarter-4
  border-radius 100% 0 0 0
  left 0
  top 0
  border-left 2px solid #000
  border-top 2px solid #000

.payment-separator
  height 2px
  width 24px
  background #E4E6EB
  border-radius 16px
  flex-shrink 0
  position absolute
  right 0
  bottom 0
  margin auto 0
  transform translate(50%, -23px)

.modal-info-section
  display flex
  flex-direction column
  gap 12px

.info-item
  display flex
  align-items flex-start
  gap 8px

  :deep(.mm-icon)
    flex-shrink 0
    margin-top 2px

.info-text
  font-size 14px
  font-weight 400
  line-height 20px
  color #76797F
</style>
